import { useState } from "react";
import { MessageCircle, Clock, ExternalLink, ChevronRight, Mail, MailOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import type { InquiryThread } from "@/services/inquiryService";

interface StudentInquiriesProps {
  inquiries: InquiryThread[];
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function StudentInquiries({ inquiries }: StudentInquiriesProps) {
  const [activeTab, setActiveTab] = useState<"all" | "unread" | "read">("all");
  const navigate = useNavigate();

  const filteredInquiries = inquiries.filter((thread) => {
    if (activeTab === "all") return true;
    if (activeTab === "unread") return thread.unreadCount > 0;
    if (activeTab === "read") return thread.unreadCount === 0;
    return true;
  });

  const unreadCount = inquiries.filter((t) => t.unreadCount > 0).length;

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-amber-50 rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 w-full">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-primary shrink-0" />
            <div>
              <h3 className="font-display text-base font-semibold text-foreground">Student Inquiries</h3>
              <p className="text-xs text-muted-foreground">
                {unreadCount > 0 ? `${unreadCount} awaiting response` : "All caught up"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-muted p-0.5 rounded-lg">
              {(["all", "unread", "read"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                    activeTab === tab
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {tab === "unread" && unreadCount > 0 && ` (${unreadCount})`}
                </button>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1 text-primary"
              onClick={() => navigate("/teacher-inquiries")}
            >
              View All
              <ExternalLink className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="px-3 py-3 space-y-1">
        {filteredInquiries.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {inquiries.length === 0 ? "No inquiries yet." : "No inquiries in this category."}
          </div>
        )}
        {filteredInquiries.map((thread) => {
          const isUnread = thread.unreadCount > 0;
          return (
            <div
              key={thread.id}
              className={cn(
                "group flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-colors",
                isUnread ? "bg-primary/5 hover:bg-primary/10" : "bg-muted/40 hover:bg-muted/60"
              )}
              onClick={() => navigate(`/teacher-inquiries?thread=${thread.id}`)}
            >
              {/* Avatar */}
              <div className={cn(
                "w-11 h-11 rounded-xl flex items-center justify-center shrink-0 text-sm font-bold",
                isUnread ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
              )}>
                {getInitials(thread.studentName || "S")}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className={cn(
                    "text-base text-foreground",
                    isUnread ? "font-semibold" : "font-medium"
                  )}>
                    {thread.studentName}
                  </span>
                  {isUnread && (
                    <Badge variant="default" className="text-xs px-2 py-0.5 shrink-0">
                      {thread.unreadCount} new
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto shrink-0">
                    <Clock className="w-3 h-3" />
                    {new Date(thread.updatedAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm font-semibold text-primary mb-0.5 truncate">{thread.subject}</p>
                <p className="text-sm text-muted-foreground truncate">
                  {thread.lastMessage?.content || "No messages yet"}
                </p>
              </div>

              {/* Right icon */}
              <div className="shrink-0">
                {isUnread
                  ? <Mail className="w-4 h-4 text-primary" />
                  : <MailOpen className="w-4 h-4 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors" />
                }
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors shrink-0" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

